import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import {
  NARRATION_AB_STATUS_COPY,
  summarizeNarrationAb,
  type NarrationAbComparison,
  type NarrationAbLane,
  type NarrationAbLine,
} from "./narrationAb";
import { formatSeconds } from "./scriptStory";

// ---------------------------------------------------------------------------
// NarrationAbPanel — the delivered voice-over beside a second model's take on
// the identical prompt. Text only, admin only, and never shown to a customer:
// this exists so we can judge a narration model change from real Echoes before
// anyone hears it.
//
// The left column is what shipped. The right column shipped nowhere.
// ---------------------------------------------------------------------------

export function NarrationAbPanel({
  comparison,
}: {
  comparison: NarrationAbComparison;
}) {
  const { primary, shadow, rows, status } = comparison;
  const comparable = rows.length > 0 && shadow.lines.length > 0;

  return (
    <div className="space-y-3" data-testid="narration-ab">
      <div className="flex flex-wrap items-center gap-2">
        <LaneBadge lane={primary} />
        <span className="text-muted-foreground text-xs">vs</span>
        <LaneBadge lane={shadow} />
        {comparison.elapsedMs !== null ? (
          <span className="text-muted-foreground text-xs">
            {Math.round(comparison.elapsedMs / 100) / 10}s
          </span>
        ) : null}
      </div>

      {!comparable ? (
        <p className="text-muted-foreground text-sm">
          {NARRATION_AB_STATUS_COPY[status]}
          {comparison.reason ? ` (${comparison.reason})` : null}
        </p>
      ) : (
        <div className="space-y-2">
          <div className="text-muted-foreground grid gap-3 text-xs font-medium sm:grid-cols-[88px_minmax(0,1fr)_minmax(0,1fr)]">
            <span />
            <span>Delivered — {primary.model}</span>
            <span>Not delivered — {shadow.model}</span>
          </div>
          {rows.map((row) => (
            <div
              key={row.key}
              className="grid gap-3 border-t pt-2 sm:grid-cols-[88px_minmax(0,1fr)_minmax(0,1fr)]"
            >
              <div className="text-muted-foreground text-xs">
                <div>{row.label}</div>
                <div>{lineStamp(row.primary ?? row.shadow)}</div>
              </div>
              <LineCell line={row.primary} />
              <LineCell line={row.shadow} />
            </div>
          ))}
          <p className="text-muted-foreground border-t pt-2 text-xs">
            {primary.lineCount} lines / {primary.wordCount} words delivered ·{" "}
            {shadow.lineCount} lines / {shadow.wordCount} words in the second
            take. The second take is recorded for review only — it is never sent
            to a customer.
          </p>
        </div>
      )}
    </div>
  );
}

/** Echo-details card wrapper. Renders nothing when no A/B was recorded. */
export function EchoNarrationAb({
  envelope,
  isAdmin,
  loading,
}: {
  envelope: Record<string, unknown> | undefined;
  isAdmin?: boolean;
  loading?: boolean;
}) {
  const comparison = useMemo(() => summarizeNarrationAb(envelope), [envelope]);
  if (!isAdmin || loading || !comparison) return null;

  return (
    <Card id="echo-narration-ab">
      <CardHeader>
        <CardTitle>Narration A/B</CardTitle>
        <p className="text-muted-foreground text-sm">
          The voice-over we shipped, beside a second model writing from the same
          brief. Internal only.
        </p>
      </CardHeader>
      <CardContent>
        <NarrationAbPanel comparison={comparison} />
      </CardContent>
    </Card>
  );
}

function LaneBadge({ lane }: { lane: NarrationAbLane }) {
  return (
    <Badge
      variant={lane.delivered ? "default" : "outline"}
      className="font-normal"
    >
      {lane.delivered ? "Delivered" : "Shadow"} · {lane.model}
    </Badge>
  );
}

function LineCell({ line }: { line: NarrationAbLine | null }) {
  if (!line) {
    return (
      <span className="text-muted-foreground text-sm italic">— no line —</span>
    );
  }
  return <p className="text-sm">{line.text}</p>;
}

function lineStamp(line: NarrationAbLine | null): string {
  return line && line.startSecond !== null
    ? formatSeconds(line.startSecond)
    : "";
}
